import { MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
/**
 * Recurring-bill proposal cards: their rows and actions (Add, Adjust, Not a bill, Add all). Since
 * TMMAPP-0141 §B they live in Planned's "Suggested recurring bills" section, which owns the heading.
 * Cadence via describeRecurrence only; no monthly normalisation of weekly/quarterly amounts.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import {
  seriesEvidence,
  seriesPayload,
  type GenesisProposal,
  type GenesisSeriesOverrides,
} from '@/api/genesis';
import { type RecurrenceRule } from '@/api/planned';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useReturnFocus } from '@/components/useConfirm';
import {
  RECURRENCE_TITLE_ID,
  RecurrencePanel,
} from '@/features/planned/recurrence/RecurrencePanel';
import { describeRecurrence, emptyRule } from '@/features/planned/recurrence/recurrence';
import { formatCivilDate, formatNumber, minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues } from '@/shared/useDirty';

const ADJUST_TITLE_ID = 'genesis-series-adjust-title';
const RECURRENCE_OPENER = 'genesis-series-recurrence';

export interface SeriesProposalsStepProps {
  proposals: GenesisProposal[];
  accounts: Account[];
  busy: boolean;
  onAccept: (id: number, overrides?: { series: GenesisSeriesOverrides }) => void;
  onDismiss: (id: number) => void;
  onAcceptAll: () => void;
}

export function SeriesProposalsStep({
  proposals,
  accounts,
  busy,
  onAccept,
  onDismiss,
  onAcceptAll,
}: SeriesProposalsStepProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const currency = me.user.currency;
  const [adjusting, setAdjusting] = useState<GenesisProposal | null>(null);

  const proposed = proposals.filter((p) => p.status === 'PROPOSED');

  const accountName = (id: number | null) =>
    id == null ? null : (accounts.find((a) => a.id === id)?.name ?? null);

  return (
    <Stack spacing={2}>
      {proposed.length > 0 && (
        <>
          <Button
            variant="outlined"
            disabled={busy}
            onClick={onAcceptAll}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('genesis.addAll', { count: proposed.length })}
          </Button>
          <Grid container spacing={2}>
            {proposed.map((p) => {
              const payload = seriesPayload(p);
              const proposalCurrency =
                accounts.find((account) => account.id === payload.accountId)?.currency ?? currency;
              const evidence = seriesEvidence(p);
              const cadence =
                payload.recurrence != null
                  ? describeRecurrence(payload.recurrence, locale, t)
                  : t('genesis.cadenceUnknown');
              const nextOn = payload.recurrence?.startOn ?? null;
              const acct = accountName(payload.accountId);
              return (
                <Grid key={p.id} size={{ xs: 12, lg: 6 }}>
                  <Box
                    component="article"
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      height: '100%',
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <Typography variant="h3">
                          {payload.payee ?? t('genesis.unnamedBill')}
                        </Typography>
                        {payload.kind != null && (
                          <Chip
                            size="small"
                            label={
                              payload.kind === 'INCOME'
                                ? t('genesis.kindIncome')
                                : t('genesis.kindExpense')
                            }
                            color={payload.kind === 'INCOME' ? 'success' : 'default'}
                          />
                        )}
                      </Stack>
                      <MoneyText minor={payload.amountMinor} currency={proposalCurrency} />
                      <Typography variant="body2">{cadence}</Typography>
                      {acct != null && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {acct}
                        </Typography>
                      )}
                      {nextOn != null && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('genesis.nextOn', { date: formatCivilDate(nextOn, locale) })}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        <Trans
                          i18nKey="genesis.seriesEvidence"
                          values={{
                            occurrences: evidence.occurrenceCount ?? 0,
                            months:
                              evidence.monthsObserved != null
                                ? formatNumber(evidence.monthsObserved, locale)
                                : '—',
                          }}
                          components={{
                            min: (
                              <MoneyText
                                minor={evidence.minAmountMinor}
                                currency={proposalCurrency}
                              />
                            ),
                            max: (
                              <MoneyText
                                minor={evidence.maxAmountMinor}
                                currency={proposalCurrency}
                              />
                            ),
                          }}
                        />
                      </Typography>
                      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busy}
                          onClick={() => onAccept(p.id)}
                        >
                          {t('genesis.add')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => setAdjusting(p)}
                        >
                          {t('genesis.adjust')}
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          disabled={busy}
                          onClick={() => onDismiss(p.id)}
                        >
                          {t('genesis.notABill')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {adjusting != null && (
        <SeriesAdjustDialog
          proposal={adjusting}
          accounts={accounts}
          busy={busy}
          onClose={() => setAdjusting(null)}
          onSave={(overrides) => {
            onAccept(adjusting.id, { series: overrides });
            setAdjusting(null);
          }}
        />
      )}
    </Stack>
  );
}

function SeriesAdjustDialog({
  proposal,
  accounts,
  busy,
  onClose,
  onSave,
}: {
  proposal: GenesisProposal;
  accounts: Account[];
  busy: boolean;
  onClose: () => void;
  onSave: (overrides: GenesisSeriesOverrides) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const currency = me.user.currency;
  const payload = seriesPayload(proposal);
  const servedRule = payload.recurrence;
  const initialStartOn = servedRule?.startOn ?? '';
  const [name, setName] = useState(payload.payee ?? '');
  const [amountText, setAmountText] = useState(
    payload.amountMinor != null
      ? minorToInputText(Math.abs(payload.amountMinor), currency, locale)
      : '',
  );
  const [accountId, setAccountId] = useState<number | ''>(payload.accountId ?? '');
  const [startOn, setStartOn] = useState(initialStartOn);
  const [rule, setRule] = useState<RecurrenceRule>(
    servedRule ?? emptyRule(initialStartOn.length > 0 ? initialStartOn : '2026-01-01'),
  );
  // The recurrence form shows in place of this form, in this same dialog (DLG-005), while it holds
  // a draft; this form's own values are back, as they were, when it closes.
  const [recurrenceDraft, setRecurrenceDraft] = useState<RecurrenceRule | null>(null);
  // The adjust form asks nothing on close (as before it was one `Dialog`); the guard exists so the
  // recurrence draft's discard question shows in this dialog, and so the one `Dialog` stays mounted.
  const guard = useDialogDirtyGuard(false, onClose);
  const recurrenceGuard = useDialogDirtyGuard(
    recurrenceDraft !== null && !sameValues(recurrenceDraft, rule),
    () => setRecurrenceDraft(null),
    false,
    0,
    recurrenceDraft !== null,
  );
  // Closing the recurrence form returns focus to the button that opened it (DLG-004).
  useReturnFocus(recurrenceDraft !== null, RECURRENCE_OPENER);

  if (recurrenceDraft !== null) {
    return (
      <GuardedDialog guard={recurrenceGuard} titleId={RECURRENCE_TITLE_ID} fullWidth maxWidth="sm">
        <RecurrencePanel
          draft={recurrenceDraft}
          setDraft={(next) =>
            setRecurrenceDraft((prev) =>
              prev === null ? null : typeof next === 'function' ? next(prev) : next,
            )
          }
          mode="series"
          focusFirst
          onCancel={recurrenceGuard.requestClose}
          onApply={() => {
            setRule(recurrenceDraft);
            setStartOn(recurrenceDraft.startOn);
            setRecurrenceDraft(null);
          }}
        />
      </GuardedDialog>
    );
  }

  return (
    <GuardedDialog guard={guard} titleId={ADJUST_TITLE_ID} fullWidth maxWidth="sm">
      <DialogTitle id={ADJUST_TITLE_ID}>{t('genesis.adjust')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('planned.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('planned.amount')}
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            fullWidth
            slotProps={{ input: { sx: { fontVariantNumeric: 'tabular-nums' } } }}
          />
          <TextField
            label={t('recurrence.startsOn')}
            type="date"
            value={startOn}
            onChange={(e) => {
              setStartOn(e.target.value);
              setRule((prev) => ({ ...prev, startOn: e.target.value }));
            }}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl fullWidth>
            <InputLabel id="genesis-series-account">{t('planned.account')}</InputLabel>
            <Select
              labelId="genesis-series-account"
              label={t('planned.account')}
              value={accountId === '' ? '' : String(accountId)}
              onChange={(e) => {
                const v = e.target.value;
                setAccountId(v === '' ? '' : Number(v));
              }}
            >
              {accounts.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            data-confirm-opener={RECURRENCE_OPENER}
            onClick={() => setRecurrenceDraft(rule)}
          >
            {describeRecurrence(rule, locale, t)}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <SubmitButton
          loading={busy}
          onClick={() => {
            const minor = parseMoneyToMinor(amountText, currency, locale);
            onSave({
              ...(name.trim().length > 0 ? { name: name.trim(), payee: name.trim() } : {}),
              ...(minor != null ? { amountMinor: Math.abs(minor) } : {}),
              ...(accountId !== '' ? { accountId } : {}),
              ...(startOn.length > 0 ? { startOn } : {}),
              recurrence: { ...rule, startOn: startOn || rule.startOn },
            });
          }}
        >
          {t('genesis.add')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
